import { useState } from 'react';
import { useParams } from 'react-router-dom';
import SplitPane from 'react-split-pane';
import { CodeEditor } from '../components/CodeEditor';
import { Play, RefreshCw } from 'lucide-react';

const problemData = {
    'arrays/1-1': {
        title: 'Two Sum',
        difficulty: 'Easy',
        description: `Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target.
You may assume that each input would have exactly one solution, and you may not use the same element twice.
You can return the answer in any order.`,
        examples: [
            {
                input: 'nums = [2,7,11,15], target = 9',
                output: '[0,1]',
                explanation: 'Because nums[0] + nums[1] == 9, we return [0, 1].'
            }
        ],
        testCases: [
            { input: '[2,7,11,15]\n9', output: '[0,1]' },
            { input: '[3,2,4]\n6', output: '[1,2]' }
        ],
        starterCode: `function twoSum(nums, target) {
    // Write your code here
}`
    }
};

export function CodeSpace() {
    const { slug, id } = useParams();
    const problem = problemData[`${slug}/${id}`];
    const [code, setCode] = useState(problem?.starterCode || '// Write your code here');
    const [testResults, setTestResults] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const runTests = () => {
        setIsRunning(true);
        setTimeout(() => {
            const results = problem?.testCases.map(testCase => {
                try {
                    const fn = new Function('return ' + code)();
                    const [nums, target] = testCase.input.split('\n');
                    const result = JSON.stringify(fn(JSON.parse(nums), parseInt(target)));
                    return {
                        passed: result === testCase.output,
                        input: testCase.input,
                        output: result,
                        expected: testCase.output
                    };
                } catch (error) {
                    return {
                        passed: false,
                        input: testCase.input,
                        output: error.message,
                        expected: testCase.output
                    };
                }
            }) || [];
            setTestResults(results);
            setIsRunning(false);
        }, 1000);
    };

    if (!problem) {
        return <div className="flex-1 p-8">Problem not found</div>;
    }

    return (
        <div className="h-screen flex flex-col lg:flex-row">
            {/* Responsive Sidebar */}
            <div
                className={`fixed lg:relative lg:flex lg:flex-col lg:w-1/3 h-full p-6 bg-white border-r overflow-y-auto z-10 transition-transform transform ${
                    isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                }`}
            >
                <button
                    className="lg:hidden mb-4 text-blue-600"
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                >
                    {isSidebarOpen ? 'Close' : 'Open'} Sidebar
                </button>
                <div className="max-w-4xl mx-auto">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-bold">{problem.title}</h1>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                            problem.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                                problem.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                        }`}>
                            {problem.difficulty}
                        </span>
                    </div>
                    <div className="prose prose-slate max-w-none">
                        <p>{problem.description}</p>
                        <h3>Examples:</h3>
                        {problem.examples.map((example, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
                                <p><strong>Input:</strong> {example.input}</p>
                                <p><strong>Output:</strong> {example.output}</p>
                                {example.explanation && (
                                    <p><strong>Explanation:</strong> {example.explanation}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content (Editor and Test Results) */}
            <div className="flex-1 flex flex-col">
                <SplitPane split="horizontal" minSize={200} defaultSize="70%" className="h-full">
                    <div className="flex-1 flex flex-col">
                        <div className="border-b bg-gray-50 p-2 flex items-center justify-between">
                            <span className="font-medium">Code Editor</span>
                            <div className="space-x-2">
                                <button
                                    onClick={runTests}
                                    disabled={isRunning}
                                    className="px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                                >
                                    {isRunning ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Play className="w-4 h-4" />
                                    )}
                                    <span>Run Tests</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 p-2">
                            <CodeEditor code={code} onChange={setCode} />
                        </div>
                    </div>

                    <div className="w-full border-t p-4 overflow-y-auto space-y-4">
                        <span className="font-medium">Test Results</span>
                        {testResults.map((result, index) => (
                            <div key={index} className={`p-4 rounded-lg ${
                                result.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                            }`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">Test Case {index + 1}</span>
                                    <span className={`px-2 py-1 rounded text-sm ${
                                        result.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                        {result.passed ? 'Passed' : 'Failed'}
                                    </span>
                                </div>
                                <div className="space-y-1 text-sm">
                                    <p><strong>Input:</strong> {result.input}</p>
                                    <p><strong>Your Output:</strong> {result.output}</p>
                                    <p><strong>Expected:</strong> {result.expected}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </SplitPane>
            </div>
        </div>
    );
}
